import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  CircularProgress,
  Pagination,
  Checkbox,
  FormControlLabel,
  ListItemText,
  TableSortLabel,
  Alert
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  Event as EventIcon,
  Delete as DeleteIcon,
  PersonAddAlt as AssignIcon,
  Download as DownloadIcon,
  School as ConvertIcon
} from '@mui/icons-material';
import { leadsAPI, salesAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { useSearchParams } from 'react-router-dom';
import { formatCalendarDate, formatDateInViewerTZ, getViewerTimezone } from '../../utils/dateTimeUtils';
import { isLeadEligibleForDemoScheduling } from '../../utils/leadDemoEligibility';
import LeadForm from './LeadForm';
import LeadDetails from './LeadDetails';
import LeadTimeline from './LeadTimeline';
import StatusUpdateDialog from './StatusUpdateDialog';
import DemoScheduleForm from './DemoScheduleForm';
import EnrollmentLinkDialog from './EnrollmentLinkDialog';
import { formatLeadParentDisplayName, formatChildAgeLabel } from './leadDisplayUtils';
import { AGE_GROUPS } from '../../constants/ageGroups';
import InfoTooltip, { LabelWithInfo } from '../../components/InfoTooltip';
import { leadsHelp } from '../../content/leadsHelp';
import toast from 'react-hot-toast';
import { getCountries } from '../../data/locationData';
import { LEAD_SOURCE_FILTER_GROUPS } from '../../constants/leadSources';

const AGING_STATUSES = ['wip_before_demo', 'demo_scheduled', 'demo_reschedule_requested', 'wip_after_demo', 'deferred'];
const countryList = getCountries();

const EMPTY_LEAD_FILTERS = {
  search: '',
  status: '',
  status_in: '',
  sub_status: '',
  country: '',
  age_group: '',
  student_type: '',
  source_group: '',
  session_type: '',
  teacher_id: '',
  demo_from: null,
  demo_to: null,
  attended_only: '',
  won_at_from: null,
  won_at_to: null,
  sales_assignees: [],
  lead_acquired_at_from: null,
  lead_acquired_at_to: null,
  include_duplicates: false,
};

function parseDateParam(v) {
  return v && dayjs(v).isValid() ? dayjs(v) : null;
}

/** Build applied filters + draft + sales selection from URL (report deep links). */
function filtersFromSearchParams(searchParams) {
  const hasReportParams = [
    'search', 'status', 'status_in', 'country', 'source_group', 'session_type', 'teacher_id',
    'assigned_to_list', 'lead_acquired_at_from', 'lead_acquired_at_to',
    'won_at_from', 'won_at_to', 'demo_from', 'demo_to', 'attended_only',
  ].some((key) => searchParams.get(key));

  if (!hasReportParams) {
    return {
      fromUrl: false,
      filters: { ...EMPTY_LEAD_FILTERS },
      draft: {
        search: '',
        status: '',
        sub_status: '',
        country: '',
        age_group: '',
        student_type: '',
        source_group: '',
        session_type: '',
        lead_acquired_at_from: null,
        lead_acquired_at_to: null,
        include_duplicates: false,
      },
      salesAssignees: [],
    };
  }

  const assignedList = (searchParams.get('assigned_to_list') || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const filters = {
    search: searchParams.get('search') || '',
    status: searchParams.get('status') || '',
    status_in: searchParams.get('status_in') || '',
    sub_status: searchParams.get('sub_status') || '',
    country: searchParams.get('country') || '',
    age_group: searchParams.get('age_group') || '',
    student_type: searchParams.get('student_type') || '',
    source_group: searchParams.get('source_group') || '',
    session_type: searchParams.get('session_type') || '',
    teacher_id: searchParams.get('teacher_id') || '',
    demo_from: parseDateParam(searchParams.get('demo_from')),
    demo_to: parseDateParam(searchParams.get('demo_to')),
    attended_only: searchParams.get('attended_only') || '',
    won_at_from: parseDateParam(searchParams.get('won_at_from')),
    won_at_to: parseDateParam(searchParams.get('won_at_to')),
    sales_assignees: assignedList,
    lead_acquired_at_from: parseDateParam(searchParams.get('lead_acquired_at_from')),
    lead_acquired_at_to: parseDateParam(searchParams.get('lead_acquired_at_to')),
    include_duplicates: searchParams.get('include_duplicates') === 'true',
  };

  return {
    fromUrl: true,
    filters,
    draft: {
      search: filters.search,
      status: filters.status,
      sub_status: filters.sub_status,
      country: filters.country,
      age_group: filters.age_group,
      student_type: filters.student_type,
      source_group: filters.source_group,
      session_type: filters.session_type,
      lead_acquired_at_from: filters.lead_acquired_at_from,
      lead_acquired_at_to: filters.lead_acquired_at_to,
      include_duplicates: filters.include_duplicates,
    },
    salesAssignees: assignedList,
  };
}

const getAgingDays = (lead) => {
  if (!AGING_STATUSES.includes(lead.status)) return null;
  const acquired = lead.lead_acquired_at ? new Date(lead.lead_acquired_at) : null;
  if (!acquired) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  acquired.setHours(0, 0, 0, 0);
  return Math.floor((today - acquired) / (24 * 60 * 60 * 1000));
};

const getDaysSinceStatusChange = (lead) => {
  const dateStr = lead.last_status_changed_at || lead.created_at;
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  d.setHours(0, 0, 0, 0);
  return Math.floor((today - d) / (24 * 60 * 60 * 1000));
};

const Leads = () => {
  const { user, isSales, isAdmin, isSalesCountryScope } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialFromUrl = useMemo(() => filtersFromSearchParams(searchParams), []); // eslint-disable-line react-hooks/exhaustive-deps -- hydrate once from landing URL
  const canCreateLead = isAdmin() || isSales();
  const canDeleteLead = isAdmin();
  const canAssignLead = isAdmin() || isSales();
  const canExportLeads = isAdmin() || isSalesCountryScope();
  const viewerTimezone = getViewerTimezone(user?.timezone);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDetailsDialog, setOpenDetailsDialog] = useState(false);
  const [openStatusDialog, setOpenStatusDialog] = useState(false);
  const [openDemoDialog, setOpenDemoDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openConvertDialog, setOpenConvertDialog] = useState(false);
  const [openAssignDialog, setOpenAssignDialog] = useState(false);
  // Used for the "Assign lead" dialog (country-filtered)
  const [salesUsers, setSalesUsers] = useState([]);
  // Used for the Leads page filter dropdown (all sales users) - lazy-loaded on dropdown open
  const [salesUsersForFilter, setSalesUsersForFilter] = useState([]);
  const [selectedSalesId, setSelectedSalesId] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);
  const [editingLead, setEditingLead] = useState(null);
  const [createPrefill, setCreatePrefill] = useState(null);
  const [loadingEditLead, setLoadingEditLead] = useState(false);
  const [deletingLeadId, setDeletingLeadId] = useState(null);
  const [filters, setFilters] = useState(() => initialFromUrl.filters);
  // Draft filter values; only applied to `filters` (and API) when user clicks Apply
  const [filterDraft, setFilterDraft] = useState(() => initialFromUrl.draft);
  const [statusMapping, setStatusMapping] = useState({});
  const [agingSort, setAgingSort] = useState({ order: 'asc', active: false });
  // Pending sales filter selection in dropdown; applied only on "Apply" click
  const [salesFilterSelection, setSalesFilterSelection] = useState(() => initialFromUrl.salesAssignees);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, pages: 1 });
  const [periodSummary, setPeriodSummary] = useState(null);
  const urlFiltersApplied = useRef(initialFromUrl.fromUrl);
  const fetchSeqRef = useRef(0);
  const lastUrlKeyRef = useRef(searchParams.toString());

  // Re-apply filters when landing from another report deep link (same /leads route, new query).
  useEffect(() => {
    const key = searchParams.toString();
    if (key === lastUrlKeyRef.current) return;
    lastUrlKeyRef.current = key;
    const parsed = filtersFromSearchParams(searchParams);
    if (!parsed.fromUrl) return;
    urlFiltersApplied.current = true;
    setFilters(parsed.filters);
    setFilterDraft(parsed.draft);
    setSalesFilterSelection(parsed.salesAssignees);
    setPage(1);
  }, [searchParams]);

  const fetchLeads = useCallback(async (opts = {}) => {
    const silent = opts.silent === true;
    // Sales users: skip fetch until "My Leads" filter is set to avoid duplicate call (no filter -> with assigned_to_list=me)
    if (user?.role === 'sales' && user?.id && (!filters.sales_assignees || filters.sales_assignees.length === 0)) {
      return;
    }
    const seq = ++fetchSeqRef.current;
    try {
      if (!silent) setLoading(true);
      const queryParams = {
        page,
        limit
      };
      
      // Add filters to query params
      if (filters.search) queryParams.search = filters.search;
      if (filters.status_in) queryParams.status_in = filters.status_in;
      else if (filters.status) queryParams.status = filters.status;
      if (filters.sub_status) queryParams.sub_status = filters.sub_status;
      if (filters.country) queryParams.country = filters.country;
      if (filters.age_group) queryParams.age_group = filters.age_group;
      if (filters.student_type) queryParams.student_type = filters.student_type;
      if (filters.source_group) queryParams.source_group = filters.source_group;
      if (filters.session_type) queryParams.session_type = filters.session_type;
      if (filters.teacher_id) queryParams.teacher_id = filters.teacher_id;
      if (filters.demo_from && dayjs(filters.demo_from).isValid()) {
        queryParams.demo_from = dayjs(filters.demo_from).format('YYYY-MM-DD');
      }
      if (filters.demo_to && dayjs(filters.demo_to).isValid()) {
        queryParams.demo_to = dayjs(filters.demo_to).format('YYYY-MM-DD');
      }
      if (filters.attended_only) queryParams.attended_only = filters.attended_only;
      if (filters.won_at_from && dayjs(filters.won_at_from).isValid()) {
        queryParams.won_at_from = dayjs(filters.won_at_from).format('YYYY-MM-DD');
      }
      if (filters.won_at_to && dayjs(filters.won_at_to).isValid()) {
        queryParams.won_at_to = dayjs(filters.won_at_to).format('YYYY-MM-DD');
      }
      if (filters.sales_assignees && filters.sales_assignees.length > 0) {
        queryParams.assigned_to_list = filters.sales_assignees.join(',');
      }
      if (filters.lead_acquired_at_from && dayjs(filters.lead_acquired_at_from).isValid()) {
        queryParams.lead_acquired_at_from = dayjs(filters.lead_acquired_at_from).format('YYYY-MM-DD');
      }
      if (filters.lead_acquired_at_to && dayjs(filters.lead_acquired_at_to).isValid()) {
        queryParams.lead_acquired_at_to = dayjs(filters.lead_acquired_at_to).format('YYYY-MM-DD');
      }
      if (filters.include_duplicates) queryParams.include_duplicates = 'true';
      
      const response = await leadsAPI.getAll(queryParams);
      // Ignore stale responses (e.g. unfiltered request finishing after filtered deep-link request)
      if (seq !== fetchSeqRef.current) return;
      setLeads(response.data?.leads || response.data || []);
      setPagination(response.data?.pagination || { page: 1, limit: 20, total: 0, pages: 1 });
      setPeriodSummary(response.data?.summary || null);
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      console.error('Error fetching leads:', error);
      toast.error(error.response?.data?.error || 'Failed to fetch leads');
    } finally {
      if (seq === fetchSeqRef.current && !silent) setLoading(false);
    }
  }, [page, limit, filters, user?.role, user?.id]);

  // Status/sub-status mapping for filter dropdowns
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await leadsAPI.getStatusSubStatusMapping();
        if (!cancelled) setStatusMapping(res.data?.mapping || {});
      } catch (e) {
        if (!cancelled) setStatusMapping({});
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const fetchSalesUsersForFilter = useCallback(async () => {
    try {
      const res = await salesAPI.getAll({ page: 1, limit: 500 });
      const allSales = res.data?.data || res.data || [];
      setSalesUsersForFilter(allSales);
    } catch (error) {
      console.error('Error fetching sales users for filter:', error);
      setSalesUsersForFilter([]);
    }
  }, []);

  // Initialize default "My Leads" filter for sales users (and sync dropdown)
  useEffect(() => {
    if (urlFiltersApplied.current) return;
    if (user?.role === 'sales' && user?.id && filters.sales_assignees.length === 0) {
      setFilters(prev => ({ ...prev, sales_assignees: ['me'] }));
      setSalesFilterSelection(['me']);
    }
  }, [user?.role, user?.id, filters.sales_assignees.length]);

  // Lazy-load sales users only when user opens the Sales User filter dropdown (removes initial /api/sales call)
  const handleSalesFilterDropdownOpen = useCallback(() => {
    if (salesUsersForFilter.length === 0) {
      fetchSalesUsersForFilter();
    }
  }, [fetchSalesUsersForFilter, salesUsersForFilter.length]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    if (searchParams.get('create') !== '1' || !canCreateLead) return;
    setEditingLead(null);
    setCreatePrefill({
      phone: searchParams.get('phone') || '',
      name: searchParams.get('name') || '',
    });
    setOpenDialog(true);
    const next = new URLSearchParams(searchParams);
    next.delete('create');
    next.delete('phone');
    next.delete('name');
    setSearchParams(next, { replace: true });
  }, [searchParams, canCreateLead, setSearchParams]);


  const handleOpenDialog = async (lead = null) => {
    if (lead) {
      setOpenDialog(true);
      setEditingLead(null);
      setLoadingEditLead(true);
      try {
        const res = await leadsAPI.getById(lead.id);
        const fullLead = res.data?.lead ?? res.data?.data?.lead ?? res.data;
        setEditingLead(fullLead && typeof fullLead === 'object' ? fullLead : lead);
      } catch (err) {
        console.error('Error fetching lead details:', err);
        toast.error(err.response?.data?.error || 'Failed to load lead details');
        setOpenDialog(false);
        setEditingLead(null);
      } finally {
        setLoadingEditLead(false);
      }
    } else {
      setEditingLead(null);
      setCreatePrefill(null);
      setOpenDialog(true);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLead(null);
    setCreatePrefill(null);
  };

  const handleOpenDetails = (lead) => {
    setSelectedLead(lead);
    setOpenDetailsDialog(true);
  };

  const handleCloseDetails = () => {
    setOpenDetailsDialog(false);
    setSelectedLead(null);
  };

  const handleOpenDeleteDialog = (lead) => {
    setSelectedLead(lead);
    setOpenDeleteDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedLead(null);
  };

  const handleDeleteLead = async () => {
    if (!selectedLead) return;

    try {
      setDeletingLeadId(selectedLead.id);
      await leadsAPI.delete(selectedLead.id);
      
      toast.success('Lead deleted successfully');
      fetchLeads(); // Refresh the leads list
      handleCloseDeleteDialog();
    } catch (error) {
      console.error('Error deleting lead:', error);
      toast.error(error.response?.data?.message || 'Failed to delete lead');
    } finally {
      setDeletingLeadId(null);
    }
  };

  const handleOpenStatusUpdate = (lead) => {
    setSelectedLead(lead);
    setOpenStatusDialog(true);
  };

  const handleCloseStatusUpdate = () => {
    setOpenStatusDialog(false);
    setSelectedLead(null);
  };

  const handleOpenDemoSchedule = async (lead) => {
    setSelectedLead(lead);
    setOpenDemoDialog(true);
    try {
      const res = await leadsAPI.getById(lead.id);
      const fullLead = res.data?.lead ?? res.data;
      if (fullLead && typeof fullLead === 'object') {
        setSelectedLead(fullLead);
      }
    } catch (err) {
      console.error('Failed to load lead for demo schedule:', err);
    }
  };

  const handleOpenAssign = async (lead) => {
    setSelectedLead(lead);
    setOpenAssignDialog(true);
    setSalesUsers([]); // Clear previous sales users
    setSelectedSalesId(''); // Reset selection
    
    try {
      const res = await salesAPI.getAll({ page: 1, limit: 100 });
      const allSalesUsers = res.data?.data || res.data || [];
      
      // Filter sales users based on lead's country
      // Only show sales users who cater to the lead's country
      let filteredSales = [];
      if (lead.country) {
        filteredSales = allSalesUsers.filter((sales) => {
          // Always include currently assigned sales user (for reassignment)
          const salesId = String(sales.user_id || sales.id);
          const assignedTo = lead.assigned_to ? String(lead.assigned_to) : null;
          if (assignedTo && salesId === assignedTo) {
            return true;
          }
          
          // If sales user has countries array and it includes the lead's country
          if (sales.countries && Array.isArray(sales.countries) && sales.countries.length > 0) {
            return sales.countries.includes(lead.country);
          }
          // If sales user has no countries set, don't show them (strict filtering)
          return false;
        });
        
        // Show info if no sales users found for this country (except current assignment)
        if (filteredSales.length === 0) {
          toast.error(`No sales users available for ${lead.country}. Please add countries to sales users first.`);
        } else if (filteredSales.length === 1 && lead.assigned_to) {
          // Only current assigned user shown - warn about limited options
          toast.warning(`Only currently assigned sales user available. Consider adding more sales users for ${lead.country}.`);
        }
      } else {
        // If lead has no country, show all sales users
        filteredSales = allSalesUsers;
        if (allSalesUsers.length > 0) {
          toast.warning('Lead has no country specified. Showing all sales users.');
        }
      }
      
      setSalesUsers(filteredSales);
      
      // Set the initial selected value AFTER sales users are loaded
      if (lead.assigned_to) {
        const assignedToStr = String(lead.assigned_to);
        // Find matching sales user to ensure the value exists
        const matchingSales = filteredSales.find(s => {
          const salesId = String(s.user_id || s.id);
          return salesId === assignedToStr;
        });
        if (matchingSales) {
          setSelectedSalesId(assignedToStr);
        } else {
          setSelectedSalesId('');
        }
      } else {
        setSelectedSalesId('');
      }
    } catch (error) {
      console.error('Error loading sales users:', error);
      toast.error('Failed to load sales users');
      setSalesUsers([]);
    }
  };

  const handleAssignLead = async () => {
    if (!selectedLead || !selectedSalesId) {
      toast.error('Please select a sales user');
      return;
    }
    try {
      setAssigning(true);
      await leadsAPI.update(selectedLead.id, { assigned_to: selectedSalesId });
      toast.success('Lead assigned successfully');
      setOpenAssignDialog(false);
      setSelectedLead(null);
      setSelectedSalesId('');
      fetchLeads();
    } catch (error) {
      console.error('Error assigning lead:', error);
      toast.error(error.response?.data?.message || 'Failed to assign lead');
    } finally {
      setAssigning(false);
    }
  };

  const handleCloseDemoSchedule = () => {
    setOpenDemoDialog(false);
    setSelectedLead(null);
  };

  const handleStatusUpdate = async () => {
    try {
      // StatusUpdateDialog has already handled the API call successfully
      // We just need to refresh the leads list and show success message
      toast.success('Status updated successfully');
      handleCloseStatusUpdate();
      fetchLeads({ silent: true });
    } catch (error) {
      // This should not happen since StatusUpdateDialog handles the API call
      console.error('Unexpected error in handleStatusUpdate:', error);
      toast.error('An unexpected error occurred');
    }
  };

  const handleDemoScheduled = () => {
    handleCloseDemoSchedule();
    fetchLeads();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'new':
        return 'primary';
      case 'wip_before_demo':
        return 'info';
      case 'demo_scheduled':
        return 'info';
      case 'demo_reschedule_requested':
        return 'warning';
      case 'demo_completed':
        return 'success';
      case 'demo_not_present':
        return 'warning';
      case 'wip_after_demo':
        return 'warning';
      case 'deferred':
        return 'default';
      case 'enrollment_pending':
        return 'info';
      case 'won':
        return 'success';
      case 'lost':
        return 'error';
      case 'dropped':
        return 'error';
      default:
        return 'default';
    }
  };

  const sortedLeads = useMemo(() => {
    if (!agingSort.active) return leads;
    const sorted = [...leads].sort((a, b) => {
      const ageA = getAgingDays(a) ?? -1;
      const ageB = getAgingDays(b) ?? -1;
      return agingSort.order === 'asc' ? ageA - ageB : ageB - ageA;
    });
    return sorted;
  }, [leads, agingSort]);

  const getStatusLabel = (status) => {
    switch (status) {
      case 'new':
        return 'New';
      case 'wip_before_demo':
        return 'WIP (before demo)';
      case 'demo_scheduled':
        return 'Demo scheduled';
      case 'demo_reschedule_requested':
        return 'Demo reschedule requested';
      case 'demo_completed':
        return 'Demo Completed';
      case 'demo_not_present':
        return 'Demo Not Present';
      case 'wip_after_demo':
        return 'WIP (after demo)';
      case 'deferred':
        return 'Deferred';
      case 'enrollment_pending':
        return 'Enrollment Pending';
      case 'won':
        return 'Won';
      case 'lost':
        return 'Lost';
      case 'dropped':
        return 'Dropped';
      default:
        return status;
    }
  };

  const canScheduleDemo = (lead) => isLeadEligibleForDemoScheduling(lead);

  const canSendEnrollmentForm = (lead) =>
    lead && lead.status !== 'won' && lead.status !== 'lost' && lead.status !== 'dropped';

  const handleOpenSendEnrollment = (lead) => {
    setSelectedLead(lead);
    setOpenConvertDialog(true);
  };

  const handleCloseConvertDialog = () => {
    setOpenConvertDialog(false);
    setSelectedLead(null);
  };

  // Update draft filter only; does not trigger fetch
  const handleFilterDraftChange = (field, value) => {
    setFilterDraft(prev => ({ ...prev, [field]: value }));
  };

  // Update only pending sales selection (no fetch until Apply)
  const handleSalesFilterSelectionChange = (e) => {
    const value = e.target.value;
    setSalesFilterSelection(typeof value === 'string' ? value.split(',') : value);
  };

  // Apply all filters (draft + sales selection) and refetch
  const handleApplyFilters = () => {
    setFilters(prev => ({
      ...prev,
      ...filterDraft,
      sales_assignees: salesFilterSelection
    }));
    setPage(1);
  };

  const handleSearchKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleApplyFilters();
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDownloadCSV = async () => {
    try {
      const params = {};
      if (filters.search) params.search = filters.search;
      if (filters.status) params.status = filters.status;
      if (filters.country) params.country = filters.country;
      if (filters.age_group) params.age_group = filters.age_group;
      if (filters.student_type) params.student_type = filters.student_type;
      if (filters.source_group) params.source_group = filters.source_group;
      if (filters.session_type) params.session_type = filters.session_type;
      if (filters.teacher_id) params.teacher_id = filters.teacher_id;
      if (filters.demo_from && dayjs(filters.demo_from).isValid()) {
        params.demo_from = dayjs(filters.demo_from).format('YYYY-MM-DD');
      }
      if (filters.demo_to && dayjs(filters.demo_to).isValid()) {
        params.demo_to = dayjs(filters.demo_to).format('YYYY-MM-DD');
      }
      if (filters.attended_only) params.attended_only = filters.attended_only;
      if (filters.won_at_from && dayjs(filters.won_at_from).isValid()) {
        params.won_at_from = dayjs(filters.won_at_from).format('YYYY-MM-DD');
      }
      if (filters.won_at_to && dayjs(filters.won_at_to).isValid()) {
        params.won_at_to = dayjs(filters.won_at_to).format('YYYY-MM-DD');
      }
      if (filters.sales_assignees && filters.sales_assignees.length > 0) {
        params.assigned_to_list = filters.sales_assignees.join(',');
      }
      if (filters.lead_acquired_at_from && dayjs(filters.lead_acquired_at_from).isValid()) {
        params.lead_acquired_at_from = dayjs(filters.lead_acquired_at_from).format('YYYY-MM-DD');
      }
      if (filters.lead_acquired_at_to && dayjs(filters.lead_acquired_at_to).isValid()) {
        params.lead_acquired_at_to = dayjs(filters.lead_acquired_at_to).format('YYYY-MM-DD');
      }
      const res = await leadsAPI.exportCSV(params);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'leads-export.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to download CSV');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Typography variant="h4" gutterBottom>
          Loading leads...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1">
          <LabelWithInfo
            label="Leads Management"
            title={leadsHelp.page.title}
            body={leadsHelp.page.body}
          />
        </Typography>
        <Box display="flex" gap={1}>
          {canExportLeads && (
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={handleDownloadCSV}
            >
              Download CSV
            </Button>
          )}
          {canCreateLead && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
            >
              Add Lead
            </Button>
          )}
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              label="Search"
              value={filterDraft.search}
              onChange={(e) => handleFilterDraftChange('search', e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Parent name, email, or phone"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={filterDraft.status}
                onChange={(e) => {
                  handleFilterDraftChange('status', e.target.value);
                  handleFilterDraftChange('sub_status', ''); // reset sub-status when status changes
                }}
                label="Status"
              >
                <MenuItem value="">Active (excl. enrolled)</MenuItem>
                <MenuItem value="all">All (incl. enrolled)</MenuItem>
                <MenuItem value="new">{getStatusLabel('new')}</MenuItem>
                <MenuItem value="wip_before_demo">{getStatusLabel('wip_before_demo')}</MenuItem>
                <MenuItem value="demo_scheduled">{getStatusLabel('demo_scheduled')}</MenuItem>
                <MenuItem value="demo_reschedule_requested">{getStatusLabel('demo_reschedule_requested')}</MenuItem>
                <MenuItem value="demo_completed">{getStatusLabel('demo_completed')}</MenuItem>
                <MenuItem value="demo_not_present">{getStatusLabel('demo_not_present')}</MenuItem>
                <MenuItem value="wip_after_demo">{getStatusLabel('wip_after_demo')}</MenuItem>
                <MenuItem value="deferred">{getStatusLabel('deferred')}</MenuItem>
                <MenuItem value="enrollment_pending">{getStatusLabel('enrollment_pending')}</MenuItem>
                <MenuItem value="won">{getStatusLabel('won')}</MenuItem>
                <MenuItem value="lost">{getStatusLabel('lost')}</MenuItem>
                <MenuItem value="dropped">{getStatusLabel('dropped')}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth disabled={!filterDraft.status || filterDraft.status === 'all'}>
              <InputLabel>Sub-status</InputLabel>
              <Select
                value={filterDraft.sub_status}
                onChange={(e) => handleFilterDraftChange('sub_status', e.target.value)}
                label="Sub-status"
              >
                <MenuItem value="">All</MenuItem>
                {(statusMapping[filterDraft.status] || []).map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Country</InputLabel>
              <Select
                value={filterDraft.country}
                onChange={(e) => handleFilterDraftChange('country', e.target.value)}
                label="Country"
              >
                <MenuItem value="">All Countries</MenuItem>
                {countryList.map((c) => (
                  <MenuItem key={c} value={c}>
                    {c}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Source</InputLabel>
              <Select
                value={filterDraft.source_group}
                onChange={(e) => handleFilterDraftChange('source_group', e.target.value)}
                label="Source"
              >
                <MenuItem value="">All Sources</MenuItem>
                {LEAD_SOURCE_FILTER_GROUPS.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Demo / Trial</InputLabel>
              <Select
                value={filterDraft.session_type}
                onChange={(e) => handleFilterDraftChange('session_type', e.target.value)}
                label="Demo / Trial"
              >
                <MenuItem value="">Any</MenuItem>
                <MenuItem value="demo">Demo</MenuItem>
                <MenuItem value="trial">Trial</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Box display="flex" alignItems="center" gap={1} sx={{ width: '100%' }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sales User</InputLabel>
                <Select
                  multiple
                  value={salesFilterSelection}
                  onChange={handleSalesFilterSelectionChange}
                  onOpen={handleSalesFilterDropdownOpen}
                  label="Sales User"
                  renderValue={(selected) => {
                    if (!selected || selected.length === 0) return 'All';
                    const parts = [];
                    if (selected.includes('me')) parts.push('My Leads');
                    if (selected.includes('unassigned')) parts.push('Unassigned');
                    const otherCount = selected.filter(s => s !== 'me' && s !== 'unassigned').length;
                    if (otherCount > 0) parts.push(`${otherCount} other${otherCount > 1 ? 's' : ''}`);
                    return parts.join(', ') || `${selected.length} selected`;
                  }}
                >
                  <MenuItem value="me">
                    <Checkbox checked={salesFilterSelection.includes('me')} />
                    <ListItemText primary="My Leads" />
                  </MenuItem>
                  <MenuItem value="unassigned">
                    <Checkbox checked={salesFilterSelection.includes('unassigned')} />
                    <ListItemText primary="Unassigned" />
                  </MenuItem>
                  {salesUsersForFilter
                    .filter((sales) => {
                      const salesUserId = String(sales.user_id || sales.id);
                      return !user || salesUserId !== String(user.id);
                    })
                    .map((sales) => {
                      const salesId = String(sales.user_id || sales.id);
                      const label = `${sales.first_name} ${sales.last_name} (${sales.email})`;
                      return (
                        <MenuItem key={salesId} value={salesId}>
                          <Checkbox checked={salesFilterSelection.includes(salesId)} />
                          <ListItemText primary={label} />
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                sx={{ flexShrink: 0 }}
              >
                Apply
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Age Group</InputLabel>
              <Select
                value={filterDraft.age_group}
                onChange={(e) => handleFilterDraftChange('age_group', e.target.value)}
                label="Age Group"
              >
                <MenuItem value="">All Age Groups</MenuItem>
                {AGE_GROUPS.map((group) => (
                  <MenuItem key={group} value={group}>{group}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth>
              <InputLabel>Student Type</InputLabel>
              <Select
                value={filterDraft.student_type}
                onChange={(e) => handleFilterDraftChange('student_type', e.target.value)}
                label="Student Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="normal">Normal</MenuItem>
                <MenuItem value="special_needs">Special Needs</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
                <DatePicker
                  label="Acquired from"
                  value={filterDraft.lead_acquired_at_from || null}
                  onChange={(date) => handleFilterDraftChange('lead_acquired_at_from', date)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  maxDate={filterDraft.lead_acquired_at_to || undefined}
                  sx={{ flex: 1 }}
                />
                <Box sx={{ pt: 1 }}>
                  <InfoTooltip title={leadsHelp.leadDateFrom.title} body={leadsHelp.leadDateFrom.body} />
                </Box>
              </Box>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={3}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.25 }}>
                <DatePicker
                  label="Acquired to"
                  value={filterDraft.lead_acquired_at_to || null}
                  onChange={(date) => handleFilterDraftChange('lead_acquired_at_to', date)}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                  minDate={filterDraft.lead_acquired_at_from || undefined}
                  sx={{ flex: 1 }}
                />
                <Box sx={{ pt: 1 }}>
                  <InfoTooltip title={leadsHelp.leadDateTo.title} body={leadsHelp.leadDateTo.body} />
                </Box>
              </Box>
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(filterDraft.include_duplicates)}
                  onChange={(e) => handleFilterDraftChange('include_duplicates', e.target.checked)}
                />
              }
              label="Show flagged duplicates"
            />
          </Grid>
        </Grid>
      </Paper>

      {(filters.won_at_from || filters.won_at_to || filters.teacher_id || filters.attended_only || filters.demo_from) && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Showing leads from a report link
          {filters.source_group ? ` · Source: ${filters.source_group}` : ''}
          {filters.session_type ? ` · ${filters.session_type === 'trial' ? 'Trial' : 'Demo'}` : ''}
          {filters.won_at_from && filters.won_at_to
            ? ` · Won ${dayjs(filters.won_at_from).format('DD MMM YYYY')}–${dayjs(filters.won_at_to).format('DD MMM YYYY')}`
            : ''}
          {filters.demo_from && filters.demo_to
            ? ` · Demo/trial date ${dayjs(filters.demo_from).format('DD MMM YYYY')}–${dayjs(filters.demo_to).format('DD MMM YYYY')}`
            : ''}
          {filters.attended_only === '1' ? ' · Attended only' : ''}
          {filters.teacher_id ? ' · Filtered by trainer' : ''}
          .
        </Alert>
      )}

      {periodSummary && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            <LabelWithInfo
              label="Period summary"
              title={leadsHelp.periodSummary.title}
              body={leadsHelp.periodSummary.body}
            />
          </Typography>
          <Typography variant="body2">
            <strong>{periodSummary.activePipeline}</strong> active pipeline leads ·{' '}
            <strong>{periodSummary.enrolledAcquiredInPeriod}</strong> enrolled (acquired in period) ·{' '}
            <strong>{periodSummary.totalAcquiredInPeriod}</strong> total acquired
            {' '}(matches Sales reports “Leads”)
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            <LabelWithInfo
              label={
                <span>
                  <strong>{periodSummary.enrollmentsClosedInPeriod}</strong> enrollments closed in this period
                </span>
              }
              title={leadsHelp.enrollmentsClosed.title}
              body={leadsHelp.enrollmentsClosed.body}
            />
            {' '}(matches Sales reports “Enrollments”)
          </Typography>
        </Alert>
      )}

      {/* Leads Table */}
      <Paper>
        {/* Pagination Info at Top */}
        {pagination.total > 0 && (
          <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ p: 2, pb: 0 }}>
            <Typography variant="body2" color="text.secondary">
              <LabelWithInfo
                label={`Showing ${((pagination.page - 1) * pagination.limit) + 1} to ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} ${filters.status === 'won' || filters.status === 'all' ? '' : 'active '}leads`}
                title={leadsHelp.listCount.title}
                body={leadsHelp.listCount.body}
              />
            </Typography>
            <Pagination
              count={pagination.pages}
              page={pagination.page}
              onChange={handlePageChange}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
            />
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Parent Name</TableCell>
                <TableCell>Child Age</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Country</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Acquired</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={agingSort.active}
                    direction={agingSort.order}
                    onClick={() => setAgingSort((p) => ({ active: true, order: p.order === 'asc' ? 'desc' : 'asc' }))}
                  >
                    Aging
                  </TableSortLabel>
                </TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedLeads.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell>
                    <Typography
                      variant="body2"
                      component="span"
                      sx={{
                        color: 'primary.main',
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': { color: 'primary.dark' }
                      }}
                      onClick={() => handleOpenDetails(lead)}
                    >
                      {lead.display_id || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" component="div" sx={{ fontWeight: 'bold' }}>
                      {(lead.mother_name || lead.father_name || '-').trim() || '-'}
                    </Typography>
                    {lead.phone && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {lead.phone}
                      </Typography>
                    )}
                    {lead.is_duplicate && (
                      <Chip size="small" label="Duplicate" color="warning" sx={{ mt: 0.5 }} />
                    )}
                    {Number(lead.family_unique_count) > 1 && (
                      <Chip
                        size="small"
                        color="info"
                        variant="outlined"
                        label={`Same parent (${lead.family_unique_count} children)`}
                        sx={{ mt: 0.5, ml: lead.is_duplicate ? 0.5 : 0 }}
                      />
                    )}
                  </TableCell>
                  <TableCell>
                    {formatChildAgeLabel(lead) || '-'}
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Chip
                        label={getStatusLabel(lead.status)}
                        color={getStatusColor(lead.status)}
                        size="small"
                        onClick={() => handleOpenStatusUpdate(lead)}
                        style={{ cursor: 'pointer', marginBottom: lead.sub_status ? '4px' : 0 }}
                      />
                      {lead.sub_status && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                          {lead.sub_status}
                        </Typography>
                      )}
                      {(() => {
                        const days = getDaysSinceStatusChange(lead);
                        if (days !== null) {
                          return (
                            <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.5 }}>
                              {days} day{days !== 1 ? 's' : ''} in status
                            </Typography>
                          );
                        }
                        return null;
                      })()}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {lead.country || '-'}
                  </TableCell>
                  <TableCell>
                    {lead.campaign_name || '-'}
                  </TableCell>
                <TableCell>
                  {lead.assigned_to ? (
                    <Chip
                      label={`Assigned: ${lead.assigned_admin_name || 'Sales User'}`}
                      size="small"
                      color="success"
                    />
                  ) : (
                    <Chip label="Unassigned" size="small" color="default" />
                  )}
                </TableCell>
                  <TableCell>{lead.source || '-'}</TableCell>
                  <TableCell>
                    {lead.lead_acquired_at
                      ? formatCalendarDate(lead.lead_acquired_at)
                      : '-'}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const days = getAgingDays(lead);
                      return days !== null ? `${days} day${days !== 1 ? 's' : ''}` : '-';
                    })()}
                  </TableCell>
                  <TableCell>
                    {formatDateInViewerTZ(lead.created_at, viewerTimezone)}
                  </TableCell>
                  <TableCell>
                    {canAssignLead && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenAssign(lead)}
                        title={lead.assigned_to ? 'Reassign Lead' : 'Assign Lead'}
                      >
                        <AssignIcon />
                      </IconButton>
                    )}
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDetails(lead)}
                      title="View Details"
                    >
                      <ViewIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleOpenDialog(lead)}
                      title="Edit Lead"
                    >
                      <EditIcon />
                    </IconButton>
                    {canScheduleDemo(lead) && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDemoSchedule(lead)}
                        title="Schedule Demo"
                      >
                        <EventIcon />
                      </IconButton>
                    )}
                    {canSendEnrollmentForm(lead) && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenSendEnrollment(lead)}
                        title="Generate enrollment link"
                      >
                        <ConvertIcon />
                      </IconButton>
                    )}
                    {canDeleteLead && (
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDeleteDialog(lead)}
                        title="Delete Lead"
                        disabled={deletingLeadId === lead.id}
                        color="error"
                      >
                        {deletingLeadId === lead.id ? <CircularProgress size={16} /> : <DeleteIcon />}
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Pagination */}
        {pagination.total > 0 && (
          <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 3, mb: 2 }}>
            <Box display="flex" alignItems="center" gap={2}>
              <Typography variant="body2" color="text.secondary">
                <LabelWithInfo
                  label={`Showing ${((pagination.page - 1) * pagination.limit) + 1} to ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total} ${filters.status === 'won' || filters.status === 'all' ? '' : 'active '}leads`}
                  title={leadsHelp.listCount.title}
                  body={leadsHelp.listCount.body}
                />
              </Typography>
              <Pagination
                count={pagination.pages}
                page={pagination.page}
                onChange={handlePageChange}
                color="primary"
                size="large"
                showFirstButton
                showLastButton
              />
            </Box>
          </Box>
        )}
      </Paper>

      {/* Dialogs */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingLead ? 'Edit Lead' : 'Add New Lead'}
        </DialogTitle>
        <DialogContent>
          {loadingEditLead ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : (
            <LeadForm
              key={editingLead?.id ?? 'new'}
              lead={editingLead}
              prefill={editingLead ? null : createPrefill}
              onSuccess={() => {
                handleCloseDialog();
                fetchLeads();
              }}
              onCancel={handleCloseDialog}
              isDialog={false}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openDetailsDialog} onClose={handleCloseDetails} maxWidth="lg" fullWidth>
        <DialogTitle>Lead Details</DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <LeadDetails open={openDetailsDialog} onClose={handleCloseDetails} lead={selectedLead} />
              </Grid>
              <Grid item xs={12} md={5}>
                <LeadTimeline leadId={selectedLead?.id} />
              </Grid>
            </Grid>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={openStatusDialog} onClose={handleCloseStatusUpdate} maxWidth="sm" fullWidth>
        <DialogTitle>Update Status</DialogTitle>
        <DialogContent>
          {selectedLead && (
            <StatusUpdateDialog
              lead={selectedLead}
              onUpdate={handleStatusUpdate}
              onCancel={handleCloseStatusUpdate}
              isDialog={false}
            />
          )}
        </DialogContent>
      </Dialog>

      <EnrollmentLinkDialog
        open={openConvertDialog}
        onClose={handleCloseConvertDialog}
        lead={selectedLead}
        onSuccess={() => fetchLeads({ silent: true })}
      />

      <Dialog open={openDemoDialog} onClose={handleCloseDemoSchedule} maxWidth="md" fullWidth>
        <DialogTitle>Schedule Demo</DialogTitle>
        <DialogContent>
          {selectedLead && (
            <DemoScheduleForm
              lead={selectedLead}
              onSuccess={handleDemoScheduled}
              onCancel={handleCloseDemoSchedule}
              isDialog={false}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog 
        open={openAssignDialog} 
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            setSelectedSalesId('');
            setOpenAssignDialog(false);
          }
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Assign Lead to Sales</DialogTitle>
        <DialogContent>
          {selectedLead && selectedLead.country && (
            <Box sx={{ mb: 2, p: 1.5, backgroundColor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Showing only sales users who cater to: <strong>{selectedLead.country}</strong>
              </Typography>
            </Box>
          )}
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Select Sales User</InputLabel>
            <Select
              value={selectedSalesId}
              onChange={(e) => setSelectedSalesId(e.target.value)}
              label="Select Sales User"
              disabled={salesUsers.length === 0}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {salesUsers.map((sales) => {
                const salesId = String(sales.user_id || sales.id);
                const countryCount = sales.countries && sales.countries.length > 0 ? sales.countries.length : 0;
                return (
                  <MenuItem key={salesId} value={salesId}>
                  {sales.first_name} {sales.last_name} ({sales.email})
                    {countryCount > 0 && ` — ${countryCount} country${countryCount !== 1 ? 's' : ''}`}
                </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {selectedLead && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Lead: {formatLeadParentDisplayName(selectedLead)} — {selectedLead.email || selectedLead.phone}
              </Typography>
              {selectedLead.country && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Lead Country: <strong>{selectedLead.country}</strong>
                </Typography>
              )}
              {!selectedLead.country && (
                <Typography variant="caption" color="warning.main" sx={{ mt: 1, display: 'block' }}>
                  ⚠️ Lead has no country specified. All sales users are shown.
                </Typography>
              )}
              {salesUsers.length === 0 && selectedLead.country && (
                <Typography variant="caption" color="error.main" sx={{ mt: 1, display: 'block' }}>
                  ⚠️ No sales users available for {selectedLead.country}. Please assign countries to sales users first.
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAssignDialog(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleAssignLead} disabled={assigning || !selectedSalesId}>
            {assigning ? 'Assigning...' : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={openDeleteDialog} onClose={handleCloseDeleteDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Lead</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the lead for{' '}
            <strong>
              {selectedLead?.first_name} {selectedLead?.last_name}
            </strong>
            ?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action will permanently close this lead and cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} disabled={deletingLeadId === selectedLead?.id}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteLead} 
            color="error" 
            variant="contained"
            disabled={deletingLeadId === selectedLead?.id}
          >
            {deletingLeadId === selectedLead?.id ? 'Deleting...' : 'Delete Lead'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Leads;
